// frontend/src/utils/apiAuth.js
//
// Authorization header for raw fetch() calls.
//
// Prefer the useApi hook, which adds this header itself and centralises 401/403
// handling. This exists for the call sites that predate it and do their own
// `res.ok` branching — every endpoint is authenticated now, so a fetch without
// the header is a 401 rather than the silent success it used to be.
//
// Reads the token from localStorage, which is where App.js keeps it, so callers
// do not have to thread googleIdToken through props that never had it.

import { toast } from 'react-hot-toast';

import { PERMISSIONS_CHANGED_EVENT } from './permissions';
import { activeT, errorText } from './apiErrors';

/**
 * Merge the bearer token into a headers object.
 *
 *   fetch(url, { method: 'POST', headers: authHeaders() })
 *   fetch(url, { headers: authHeaders({ 'Content-Type': 'application/json' }) })
 *
 * Returns the extra headers unchanged when no token is stored: an
 * `Authorization: Bearer null` would be a confusing 401, and the request is
 * going to fail either way.
 */
export const authHeaders = (extra = {}) => {
    const token = localStorage.getItem('googleIdToken');
    return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra };
};

// Backend refusals that mean the caller's own access changed, rather than the
// caller simply asking for something they were never allowed. Every endpoint is
// authorized server-side now, so a role change lands as one of these on the
// next request — the cached profile in localStorage is stale at that point and
// the UI is still offering actions that will keep failing.
const STALE_PERMISSION_CODES = new Set([
    'access_denied',
    'access_disabled',
    'insufficient_permissions',
    'invalid_role',
]);

/**
 * For a 403 body whose code says the caller's own access changed: say so once and
 * let App.js re-verify, so the UI stops offering controls the backend refuses.
 * Codes like customer_out_of_scope are a normal refusal and are left to the caller.
 */
export const notifyIfAccessChanged = (responseData) => {
    if (!STALE_PERMISSION_CODES.has(responseData?.error)) return;
    // No `t` prop here; activeT() is the language App.js is currently rendering in.
    toast.error(errorText(responseData, activeT(), 'Your access has changed.'), {
        id: 'permissions-changed',
    });
    window.dispatchEvent(new CustomEvent(PERMISSIONS_CHANGED_EVENT, { detail: responseData }));
};

/**
 * fetch() with the bearer token and useApi's session handling: a 401, or a 403
 * saying access changed, makes App.js re-verify the session (logging out if it
 * is gone). The Response comes back untouched, so callers keep their own
 * res.ok / blob / errorText handling.
 *
 *   apiFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body })
 */
export const apiFetch = async (url, options = {}) => {
    const res = await fetch(url, { ...options, headers: authHeaders(options.headers) });
    if (res.status === 401) {
        window.dispatchEvent(new CustomEvent(PERMISSIONS_CHANGED_EVENT, { detail: { status: 401 } }));
    } else if (res.status === 403) {
        notifyIfAccessChanged(await res.clone().json().catch(() => ({})));
    }
    return res;
};
